import React, { useEffect, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { setAppLoading } from "src/slices/bkvReducer";
import store, { useDispatch } from "src/store";

function LocationUpdater({
  setIsLocationFetched,
}: {
  setIsLocationFetched: Function;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { planType } = useParams();

  const DEFAULT_ZIPCODE = "77005";
  const dispatch = useDispatch();
  const state = store.getState().bkv;
  const updateUrl = useCallback(
    (newZipCode: string) => {
      const currentParams = new URLSearchParams(location.search);
      const currentZipcode = currentParams.get("zipcode");
      if (currentZipcode !== newZipCode) {
        currentParams.set("zipcode", newZipCode);
        const newUrl = `/${planType}?${currentParams.toString()}`;
        console.log("Updating URL to:", newUrl);
        navigate(newUrl, { replace: true });
        // Reload the page to ensure everything is reset properly
      }
    },
    [navigate, location, planType]
  );

  const getZipCode = useCallback(
    async (latitude: number, longitude: number) => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
          {
            headers: {
              "User-Agent": "YourAppName/1.0",
            },
          }
        );
        const data = await response.json();
        const newZipCode = data.address?.postcode;

        if (newZipCode) {
          // console.log("Found zipcode:", newZipCode);
          updateUrl(newZipCode);
        } else {
          // console.log("Couldn't find zipcode for this location, using default");
          updateUrl(DEFAULT_ZIPCODE);
        }
      } catch (err) {
        console.error("Error getting zipcode:", err);
        updateUrl(DEFAULT_ZIPCODE);
      }
    },
    [updateUrl]
  );

  const requestLocation = useCallback(() => {
    const currentParams = new URLSearchParams(location.search);
    const currentZipcode = currentParams.get("zipcode");
    // If zipcode already in url no need to fetch location
    if (currentZipcode) {
      setIsLocationFetched(true);
      return;
    }
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // console.log("Received position:", position.coords);
          getZipCode(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.error("Geolocation error:", error.code, error.message);
          updateUrl(DEFAULT_ZIPCODE);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      console.log("Geolocation is not available");
      updateUrl(DEFAULT_ZIPCODE);
    }
  }, [getZipCode, updateUrl]);

  useEffect(() => {
    // console.log("LocationUpdater mounted, requesting location...");
    requestLocation();
  }, [requestLocation]);

  return null; // This component doesn't render anything
}

export default LocationUpdater;
